import React from 'react'
import { Helmet } from 'react-helmet-async'
import twitterImage from '@/assets/images/brand/brand_seo_lumar_twitter.jpeg'
import facebookImage from '@/assets/images/brand/brand_seo_lumar_facebook.jpeg'

interface SEOProps {
  title: string
  description: string
  url: string
  keywords?: string[]
  language?: string
  author?: string
  publishedTime?: string
  modifiedTime?: string
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  url,
  keywords = [],
  language = 'en',
  author = '© 2024 Ab4cus Tecnología',
  publishedTime,
  modifiedTime
}) => {
  const siteName = 'Lumar Prestige'
  const twitterHandle = '@LumarPrestige' // Replace with your actual Twitter handle

  return (
    <Helmet>
      {/* General tags */}
      <html lang={language} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(', ')} />
      <link rel="canonical" href={url} />

      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={twitterImage} />

      {/* Facebook Open Graph tags */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={facebookImage} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />

      {/* Additional SEO best practices */}
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="robots" content="index, follow" />

      {/* Copyright and authorship */}
      <meta name="copyright" content={`© ${new Date().getFullYear()} ${siteName}`} />
      <meta name="author" content={author} />

      {/* Article metadata */}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* JSON-LD structured data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "http://schema.org",
          "@type": "WebPage",
          name: title,
          description: description,
          url: url,
          author: {
            "@type": "Organization",
            name: author
          },
          publisher: {
            "@type": "Organization",
            name: siteName
          },
          datePublished: publishedTime,
          dateModified: modifiedTime || publishedTime
        })}
      </script>
    </Helmet>
  )
}

export default SEO